"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.BlockConversionError = void 0;
exports.blocksToDelta = blocksToDelta;
/**
 * BLOCKS → ONE CONTINUOUS DELTA.
 *
 * The single implementation. It was written for the corpus migration and is
 * verified by an EQUIVALENCE GATE over all 76 stored legal documents
 * (`documents-ext-service/src/__tests__/deltaMigration.test.ts`): each one is
 * rendered both ways and the HTML must be byte-identical after a whitelist
 * normalisation, with matching PDF page counts and matching heading anchors.
 *
 * It lives HERE rather than in the migration script so the editor can convert
 * a legacy block document for editing using the exact code the gate verifies.
 * Two implementations would mean the thing the gate proves and the thing an
 * author edits are not the same thing.
 *
 * PURE. No DOM, no Quill, no rendering — types only from this package.
 */
/** Raised when a block cannot be represented. */
class BlockConversionError extends Error {
    constructor(message) {
        super(message);
        this.name = 'BlockConversionError';
    }
}
exports.BlockConversionError = BlockConversionError;
/**
 * The renderer's token pattern, restated. Kept identical to `TOKEN` in
 * documents-render/src/merge.ts — a name this misses stays a literal `{{`.
 */
const MERGE_TOKEN = /\{\{\s*([a-zA-Z][a-zA-Z0-9_]*)\s*\}\}/g;
/**
 * The anchor a cross-reference points at.
 *
 * Blocks are going away, so `refBlockId` cannot keep pointing at a block id —
 * but the 474 anchored references in the corpus must keep resolving, so the
 * anchor is DERIVED FROM the block id rather than freshly minted. Identity is
 * preserved verbatim: `#b056` in the block HTML is `#b056` in the Delta HTML,
 * so an external link or bookmark into a document survives the migration.
 */
const anchorFor = (blockId) => blockId;
/**
 * `{{token}}` → a `mergeField` embed; everything else stays text.
 *
 * A token must NOT survive as literal text: that is the whole point of the
 * embed, and a literal `{{` in an artifact is a defect the renderer throws on.
 */
function textOps(raw, attributes) {
    const ops = [];
    const push = (s) => {
        if (s === '')
            return;
        ops.push((attributes ? { insert: s, attributes } : { insert: s }));
    };
    let last = 0;
    MERGE_TOKEN.lastIndex = 0;
    for (const m of raw.matchAll(MERGE_TOKEN)) {
        push(raw.slice(last, m.index));
        ops.push({ insert: { mergeField: { name: m[1] } } });
        last = (m.index ?? 0) + m[0].length;
    }
    push(raw.slice(last));
    if (ops.length === 0) {
        ops.push((attributes ? { insert: '', attributes } : { insert: '' }));
    }
    return ops.filter((o) => o.insert !== '');
}
/**
 * Inline marks → Delta inline attributes. `ref` is not a mark in the Delta
 * model — it becomes a `docRef` EMBED — and is handled by the caller.
 */
function inlineAttributes(run) {
    const attrs = {};
    const marks = run.marks ?? [];
    if (marks.includes('bold'))
        attrs.bold = true;
    if (marks.includes('italic'))
        attrs.italic = true;
    if (marks.includes('underline'))
        attrs.underline = true;
    if (marks.includes('link')) {
        if (!run.href)
            throw new BlockConversionError('a link run carries no href');
        attrs.link = run.href;
    }
    return Object.keys(attrs).length ? attrs : undefined;
}
/**
 * One inline run → ops. A `ref` run becomes a `docRef` embed carrying the
 * anchor and its last known wording as the fallback.
 */
function runOps(run) {
    if (run.marks?.includes('ref')) {
        if (!run.refBlockId)
            throw new BlockConversionError('a ref run carries no refBlockId');
        return [{
                insert: { docRef: { anchorId: anchorFor(run.refBlockId), text: run.text } },
            }];
    }
    return textOps(run.text, inlineAttributes(run));
}
/** The inline content of a block: its runs when it has them, its text otherwise. */
function inlineOps(text, runs) {
    if (runs)
        return runs.flatMap(runOps);
    return textOps(text);
}
/**
 * A line terminator. `align: 'left'` emits NOTHING — left is the ABSENCE of an
 * attribute, and writing it would create a second encoding of the default and
 * make a lossless round trip look lossy.
 */
function lineBreak(attrs) {
    const a = { ...attrs };
    if (a.align !== 'center' && a.align !== 'right')
        delete a.align;
    for (const k of Object.keys(a))
        if (a[k] === undefined)
            delete a[k];
    return (Object.keys(a).length ? { insert: '\n', attributes: a } : { insert: '\n' });
}
function convertBlock(block) {
    const b = block;
    switch (b.type) {
        case 'heading': {
            if (![1, 2, 3].includes(b.level)) {
                throw new BlockConversionError(`heading level ${b.level}`);
            }
            // EVERY heading carries an anchor, not only the referenced ones. An
            // anchor is what a future cross-reference attaches to, adding one later
            // would need a migration of its own, and it keeps the element id in the
            // rendered HTML identical to the block path's.
            return [
                ...inlineOps(b.text, b.runs),
                lineBreak({ header: b.level, anchorId: anchorFor(b.id), align: b.align }),
            ];
        }
        case 'paragraph':
            return [...inlineOps(b.text, b.runs), lineBreak({ align: b.align })];
        case 'list': {
            const list = b.ordered ? 'ordered' : 'bullet';
            return b.items.flatMap((item, i) => {
                const runs = b.itemRuns?.[i];
                const ops = runs ? runs.flatMap(runOps) : textOps(item);
                return [...ops, lineBreak({ list, align: b.align })];
            });
        }
        // ── block embeds: each stands between lines and carries its own
        // terminating newline, exactly as a block embed does in Quill.
        case 'callout':
            return [
                { insert: { callout: { tone: b.tone, content: inlineOps(b.text, b.runs) } } },
                { insert: '\n' },
            ];
        case 'table':
            return [
                { insert: { table: { headers: b.headers, rows: b.rows } } },
                { insert: '\n' },
            ];
        case 'signatureField': {
            const s = { signerRole: b.signerRole };
            // Copied only when PRESENT. Defaulting `showName`/`showDate` here would
            // add a printed-name line to a contract that never had one — a content
            // change disguised as a schema default.
            for (const k of ['label', 'nameLabel', 'titleLabel', 'dateLabel']) {
                if (b[k] !== undefined)
                    s[k] = b[k];
            }
            for (const k of ['showName', 'showTitle', 'showDate']) {
                if (b[k] !== undefined)
                    s[k] = b[k];
            }
            return [{ insert: { signatureField: s } }, { insert: '\n' }];
        }
        case 'mergeField': {
            const m = { name: b.field };
            if (b.label !== undefined)
                m.label = b.label;
            return [{ insert: { mergeFieldBlock: m } }, { insert: '\n' }];
        }
        case 'pageBreak':
            return [{ insert: { pageBreak: true } }, { insert: '\n' }];
        default:
            throw new BlockConversionError(`unsupported block type ${JSON.stringify(b?.type)}`);
    }
}
/**
 * Merge adjacent PLAIN string inserts.
 *
 * Real Quill collapses consecutive unattributed lines into one op — it emits
 * `{ insert: 'a\n\nb\n' }`, never three ops — so the converted document is
 * canonicalised the same way. Purely cosmetic for the renderers (they split on
 * `\n` across the whole op stream either way), but a Delta that does not look
 * like Quill's own output invites someone to conclude the editor corrupted it
 * on the first save.
 */
function coalesce(ops) {
    const out = [];
    for (const op of ops) {
        const prev = out[out.length - 1];
        const cur = op;
        if (prev && typeof prev.insert === 'string' && prev.attributes === undefined
            && typeof cur.insert === 'string' && cur.attributes === undefined) {
            out[out.length - 1] = { insert: prev.insert + cur.insert };
            continue;
        }
        out.push(op);
    }
    return out;
}
function blocksToDelta(blocks) {
    const ops = coalesce(blocks.flatMap(convertBlock));
    const last = ops[ops.length - 1];
    // The document MUST end with a newline: a line attribute rides on the
    // newline that terminates its line, so a final line without one has nowhere
    // to put its heading, list marker or alignment.
    if (!(typeof last?.insert === 'string' && last.insert.endsWith('\n'))) {
        ops.push({ insert: '\n' });
    }
    return { ops };
}
//# sourceMappingURL=blocksToDelta.js.map